import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MeetingChapter, MeetingDetail, TranscriptLine } from '@contracts/meetings';
import { Card } from '../ui';
import { API_ORIGIN } from '../api/client';

/**
 * The recording, with the meeting's sections under it (PRD 55).
 *
 * Vladimir, 2026-09-22: *"sync video with transcript and I want to add
 * sections to the video, like time codes of the start of next sections and
 * buttons to quickly switch between them"*.
 *
 * ## The sections already existed
 *
 * They are the walkthrough's chapters (PRD 47) — written once, after the
 * meeting, with a title and the span of transcript they retell. Nothing new is
 * asked of the model for this; what was missing was a clock they and the video
 * could share.
 *
 * ## Three clocks, one origin
 *
 * The video starts when the notetaker joined. A transcript line carries an
 * absolute time. A chapter's `at` counts from the first word **spoken**, which
 * can be a minute after the join. `meeting.video.startedAt` is the wall-clock
 * moment of 00:00, written by the archiver from the run whose video was kept,
 * and everything here is measured from it.
 *
 * A chapter is placed by the **line it starts on** rather than by its own `at`,
 * because that line's `spokeAt` is a real observation and `at` is a derived
 * offset from a different origin. `at` is only the fallback for a chapter whose
 * starting line is gone.
 *
 * ## On the Walkthrough tab, not the Transcript
 *
 * Vladimir, 2026-09-24: *"Move video with buttons timecodes from transcript to
 * walkthrough tab"*. The sections ARE the walkthrough's chapters, so the video
 * now sits above them, and the transcript is words only. A transcript line's
 * time still opens the recording at that moment — on this tab.
 */
export function MeetingPlayer({
  meeting,
  chapters,
  lines,
  seekTo,
}: {
  meeting: MeetingDetail;
  chapters: MeetingChapter[];
  lines: TranscriptLine[];
  /** A seek asked for elsewhere. The nonce is what lets the same second be asked for twice. */
  seekTo: { seconds: number; nonce: number } | null;
}): ReactElement | null {
  const video = useRef<HTMLVideoElement>(null);
  const strip = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(0);
  const zero = meeting.video.startedAt ? new Date(meeting.video.startedAt).getTime() : null;

  const marks = useMemo(() => sections(chapters, lines, zero), [chapters, lines, zero]);

  useEffect(() => {
    if (!seekTo || !video.current) return;
    video.current.currentTime = seekTo.seconds;
    // The strip says where it went at once, not only once the video has
    // loaded far enough to report the seek back.
    setAt(seekTo.seconds);
    // A seek asked for from the transcript or the walkthrough means "show me
    // this" — so it plays. Pausing and seeking is what the player's own
    // controls are for.
    void video.current.play().catch(() => {
      // Autoplay refused (no gesture yet, or a policy). The seek still landed,
      // which is the part that was asked for.
    });
  }, [seekTo]);

  if (!meeting.video.available) return null;

  // The last one the playhead has reached. A quarter-second of slack, because
  // a seek lands a frame short of the second it was asked for and the strip
  // would light the section before.
  let here = -1;
  for (let i = 0; i < marks.length; i += 1) if (at + 0.25 >= marks[i].seconds) here = i;

  return (
    <Card flush className="player">
      <video
        ref={video}
        controls
        preload="metadata"
        className="player-video"
        src={`${API_ORIGIN}/v1/meetings/${meeting.id}/video`}
        onTimeUpdate={(e) => setAt(e.currentTarget.currentTime)}
        onSeeked={(e) => setAt(e.currentTarget.currentTime)}
      />
      {marks.length > 0 ? (
        <div
          className="player-marks"
          ref={strip}
          role="group"
          aria-label="Sections of this meeting"
        >
          {marks.map((m, i) => (
            <button
              key={m.id}
              type="button"
              className={`player-mark ${i === here ? 'on' : ''}`}
              aria-current={i === here ? 'true' : undefined}
              // ⚠ Only the strip is scrolled, never the page: `scrollIntoView`
              // would walk up and pull the video itself out of view, which is
              // the one thing somebody watching it is looking at.
              ref={
                i === here
                  ? (el) => {
                      const box = strip.current;
                      if (!el || !box) return;
                      const left = el.offsetLeft - box.offsetLeft;
                      if (
                        left < box.scrollLeft ||
                        left + el.offsetWidth > box.scrollLeft + box.clientWidth
                      )
                        box.scrollTo({ left: left - 12, behavior: 'smooth' });
                    }
                  : undefined
              }
              onClick={() => {
                if (!video.current) return;
                video.current.currentTime = m.seconds;
                setAt(m.seconds);
                void video.current.play().catch(() => {});
              }}
            >
              <span className="player-mark-title">{m.title}</span>
              <span className="mono player-mark-at">{timecode(m.seconds)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

/** One section, placed on the video's clock. */
export interface Section {
  id: string;
  title: string;
  seconds: number;
}

/**
 * The chapters, in order, each at the second of the video it begins on.
 *
 * Pure. A chapter is dropped rather than guessed at when there is no honest
 * way to place it — a strip with a section in the wrong place is worse than a
 * strip with one section fewer, because the wrong one is the one people press.
 */
export function sections(
  chapters: MeetingChapter[],
  lines: TranscriptLine[],
  zero: number | null,
): Section[] {
  if (zero === null) return [];
  const spokeAt = new Map(lines.filter((l) => l.spokeAt).map((l) => [l.id, l.spokeAt!]));
  // The offset between the two origins, for chapters whose starting line is
  // gone: `at` counts from the first word spoken, so adding when that word was
  // puts it back on the video's clock.
  const firstWord = lines.find((l) => l.spokeAt)?.spokeAt;
  const drift = firstWord ? new Date(firstWord).getTime() - zero : null;

  return chapters
    .map((c) => {
      const line = c.startSegmentId ? spokeAt.get(c.startSegmentId) : undefined;
      const ms = line ? new Date(line).getTime() - zero : drift === null ? null : c.at + drift;
      return ms === null ? null : { id: c.id, title: c.title, seconds: Math.max(0, ms / 1000) };
    })
    .filter((s): s is Section => s !== null)
    .sort((a, b) => a.seconds - b.seconds);
}

/** `12:04`, or `1:02:40` once a meeting has run over the hour. */
export function timecode(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Where in the video a transcript line is, in seconds — null when it cannot be known. */
export function secondsOfLine(line: TranscriptLine, zero: number | null): number | null {
  if (zero === null || !line.spokeAt) return null;
  return Math.max(0, (new Date(line.spokeAt).getTime() - zero) / 1000);
}
